(ns hai.tasks.drawer
  "The drawer along the foot of the task card. Its tray opens fitted to its
   list, between one row and half the card, and stays so until it is dragged
   by its title row; dragged below its floor it is left as a strip with a
   button that brings its list back. Its tabs open and close the tray and turn
   it between what the task relates to, grouped by kind, and who the task is
   shared with. Each open drawer ends in the field that adds to it. With the
   drawer shut, Related's add button brings Related's field out on its own,
   and the Share button in the task's header brings Share's."
  (:require
   [clojure.string :as str]
   [hai.contacts.helpers :as contacts]
   [hai.entity.related :as related]
   [hai.entity.sections :as sections]
   [hai.host-page :as host-page]
   [hai.tasks.drawer.core :as core]
   [hai.tasks.drawer.data :as data]
   [hai.tasks.drawer.parts :as parts]
   [hai.tasks.drawer.search-row :as search-row]
   [hai.tasks.drawer.share :as share]
   [hai.tasks.drawer.share-bar :as share-bar]
   [hai.ui.components.tags :as tag-ui]
   [hai.ui.icons :as icons]
   [hai.ui.rf-sugar :refer [<s >e >e!]]
   [hai.ui.tokens :as t]
   [lambdaisland.ornament :as o]
   [reagent.core :as r]
   [url.core :as url]
   [url.scope :as url-scope]))

(def ^:private space-icon (icons/mui-icon :outlined :workspaces))
(def ^:private contact-icon (icons/mui-icon :outlined :person))
(def ^:private share-icon (icons/mui-icon :outlined :share))

(def ^:private more-icon (icons/mui-icon :outlined :more_horiz))

;; What a tray dragged below its floor is left as.
(def ^:private tray-strip-height 16)
;; What a tray dragged all the way up leaves of the description above it: the
;; least height [[hai.tasks.form]]'s card layout gives the description.
(def ^:private description-strip-height 16)
;; How far down the card the tray's top comes to rest when it is taken back off
;; a description too long to show whole.
(def ^:private tray-rest-mark 0.6)
(def ^:private result-limit 5)

;;
;; Styles
;;

;; A stacking context of its own: what an open search finds lies over the tray.
(o/defstyled drawer-frame :div
  {:position "relative" :z-index "0"
   :display "flex" :flex-direction "column"
   :flex "0 1 auto" :min-height "0"}
  ;; Over the strip of the description a tray dragged all the way up leaves:
  ;; the … that takes the tray back down off it. Solid, in the card's colour,
  ;; so the line of text the strip cuts through doesn't show.
  [:.description-more
   {:position "absolute" :left "0" :right "0" :bottom "100%"
    :height "16px" :padding "0"
    :display "flex" :align-items "center" :justify-content "center"
    :border "none"
    :background t/--card-surface
    :color t/--link-muted
    :cursor "pointer"}
   [:svg {:width "20px" :height "20px" :fill "currentColor"}]
   [:&:hover {:color t/--card-forest}]])

;; The tray's top edge: what the tray shows by name, the line it is dragged by
;; laid across its middle, and its ✕. The whole row takes the drag.
(o/defstyled tray-head :div
  {:position "relative"
   :flex "none"
   :display "flex" :align-items "center" :gap "4px"
   :box-sizing "border-box"
   :height "32px"
   :padding "0 6px 0 13px"
   :background t/--task-drawer-surface
   :border-width "1px 1px 0" :border-style "solid" :border-color t/--property-3
   :border-radius "16px 16px 0 0"
   ;; Upward only: the row is positioned, so a shadow spreading down would lie
   ;; across the tray's first rows.
   :box-shadow "0 -8px 14px -8px rgba(45,62,42,0.14)"
   :cursor "ns-resize"
   :touch-action "none"
   :user-select "none"}
  [:.tray-name
   {:flex "1 1 auto" :min-width "0"
    :display "flex" :align-items "center" :gap "6px"
    :font-size "12.5px" :font-weight "800"
    :white-space "nowrap"
    :color t/--card-forest}
   [:svg {:flex "none" :width "14px" :height "14px" :fill "currentColor"}]]
  [:.tray-handle
   {:position "absolute" :left "50%" :top "50%"
    :transform "translate(-50%, -50%)"
    :width "52px" :height "4px"
    :border-radius "2px"
    :background t/--property-3
    :pointer-events "none"
    :transition "background 0.16s ease"}]
  [:.tray-close
   {:flex "none"
    :width "22px" :height "22px" :padding "0"
    :display "inline-flex" :align-items "center" :justify-content "center"
    :border "none" :border-radius "6px"
    :background "transparent"
    :color t/--link-muted
    :cursor "pointer"}
   [:&:hover {:background t/--property-3 :color t/--card-forest}]
   [:svg {:width "13px" :height "13px" :fill "currentColor"}]]
  [:at-media {:hover "hover"}
   [:&:hover [:.tray-handle {:background t/--card-sage}]]]
  [:&.dragging [:.tray-handle {:background t/--card-forest}]])

(o/defstyled tray :div
  {:flex "0 1 auto" :min-height "0"
   :box-sizing "border-box"
   :padding "4px 10px 9px"
   :overflow-y "auto" :overflow-x "hidden"
   :scrollbar-gutter "stable"
   :scrollbar-width "thin"
   :scrollbar-color "var(--note-sage-55) var(--card-forest-10)"
   :background t/--task-drawer-surface
   :border-width "0 1px" :border-style "solid" :border-color t/--property-3}
  ;; Dragged below its floor: a strip showing nothing of its list, only the
  ;; button that brings it back. The list keeps its room, unseen, so the tray
  ;; can still measure the whole of it.
  [:&.minimised
   {:position "relative"
    :overflow "hidden"}
   [#{:.tray-groups :.tray-empty} {:visibility "hidden"}]]
  [:.tray-more
   {:position "absolute" :inset "0"
    :display "flex" :align-items "center" :justify-content "center"
    :padding "0"
    :border "none"
    :background "transparent"
    :color t/--link-muted
    :cursor "pointer"}
   [:svg {:width "20px" :height "20px" :fill "currentColor"}]
   [:&:hover {:color t/--card-forest}]]
  ;; While more of the list lies below: a strip stuck to the tray's foot whose
  ;; … moves the list on a view. It is the tray's own colour, solid, and runs
  ;; out over the tray's side padding, so nothing of the list shows through or
  ;; beside it; the whole strip takes the press. Sticking at 0 stops at the
  ;; tray's bottom padding, with the list showing in the 9px under it: the
  ;; offset takes the strip down over that padding, to the tray's edge.
  [:.tray-below
   {:position "sticky" :bottom "-9px"
    :height "0"}]
  [:.tray-page
   {:position "absolute" :left "-10px" :right "-10px" :bottom "0"
    :height "20px" :padding "0"
    :display "flex" :align-items "center" :justify-content "center"
    :border "none"
    :background t/--task-drawer-surface
    :color t/--link-muted
    :cursor "pointer"}
   [:svg {:width "20px" :height "20px" :fill "currentColor"}]
   [:&:hover {:color t/--card-forest}]]
  [:.tray-groups
   {:display "flex" :flex-direction "column" :gap "10px"}]
  [:.tray-group
   {:display "flex" :flex-direction "column" :gap "6px"}]
  [:.tray-empty
   {:padding "4px 4px 2px"
    :font-size "12px" :font-style "italic"
    :color t/--link-muted}])

(o/defstyled group-heading :div
  {:display "flex" :align-items "center" :gap "5px"
   :line-height "1"
   :font-size "10px" :font-weight "800"
   :letter-spacing "0.07em" :text-transform "uppercase"
   :color t/--link-muted}
  [:.group-heading-icon
   {:display "inline-flex"
    :color t/--card-forest}
   [:svg {:width "12px" :height "12px" :fill "currentColor"}]])

(o/defstyled add-page-button :button
  {:display "inline-flex" :align-items "center" :gap "5px"
   :box-sizing "border-box"
   :height "24px" :min-width "0" :max-width "100%"
   :padding "0 11px 0 8px"
   :border-width "1.5px" :border-style "solid" :border-color t/--humble-primary-dark
   :border-radius "999px"
   :background "transparent"
   :font-family "inherit" :font-size "11.5px" :font-weight "800"
   :white-space "nowrap"
   :color t/--humble-primary-dark
   :cursor "pointer"}
  [:svg {:flex "none" :width "14px" :height "14px" :fill "currentColor"}]
  [:at-media {:hover "hover"}
   [:&:hover {:background t/--card-violet-soft}]])

(o/defstyled add-link-button :button
  {:flex "none"
   :display "inline-flex" :align-items "center" :gap "4px"
   :height "24px" :padding "0 9px"
   :border "none" :border-radius "7px"
   :background t/--humble-primary-dark
   :font-family "inherit" :font-size "10.5px" :font-weight "800"
   :letter-spacing "0.04em" :text-transform "uppercase"
   :color "white"
   :cursor "pointer"}
  [:svg {:width "13px" :height "13px" :fill "currentColor"}]
  [:&:hover {:background t/--humble-primary}])

;; The tab's own button and what else is pressed on the tab: the related pill
;; after what it says, and, where the viewer may add, an add button at its
;; right end. One button can't hold another, so the tab's own stands beside
;; them and takes the rest of the tab from under them.
(o/defstyled tab :div
  {:position "relative"
   :flex "1 1 0" :min-width "0"
   :display "flex"
   :height "34px"
   :background t/--task-drawer-surface
   :box-shadow "inset 1px 0 0 var(--property-3), inset -1px 0 0 var(--property-3)"
   :color t/--card-forest}
  [:.tab-face
   {:flex "1 1 auto" :min-width "0"
    :display "flex" :align-items "center" :gap "6px"
    :padding "0 14px"}]
  [:&.with-add [:.tab-face {:padding-right "6px"}]]
  [:.tab-press
   {:min-width "0"
    :display "flex" :align-items "center" :gap "6px"
    :padding "0"
    :border "none"
    :background "transparent"
    :font-family "inherit" :font-size "12px" :font-weight "700"
    :white-space "nowrap"
    :color "inherit"
    :cursor "pointer"}
   [:&::after {:content "''" :position "absolute" :inset "0"}]]
  ;; Positioned, so it lies over the tab's press. On the tab it is the count
  ;; alone, without the lead glyph.
  [related/pill {:position "relative"}
   [:.pill-glyph {:display "none"}]]
  [:.tab-label
   {:min-width "0"
    :overflow "hidden" :text-overflow "ellipsis"}]
  [:.tab-glyph
   {:display "inline-flex"}
   [:svg {:width "14px" :height "14px" :fill "currentColor"}]]
  [:.tab-count {:font-weight "800"}]
  ;; The tab not shown sits behind the tray: darker, and edged all round.
  [:&.behind
   {:background t/--card-forest-10
    :box-shadow "inset 0 0 0 1px var(--property-3)"
    :color t/--link-muted}
   [:&:hover {:color t/--card-forest}]]
  ;; Two tabs round the corners they meet at, which leaves a notch between
  ;; them at the card's edge.
  [:&.leading {:border-bottom-right-radius "10px"}]
  [:&.trailing {:border-bottom-left-radius "10px"}]
  ;; The leading tab takes 55% of the strip: Related has its count and the
  ;; longer add button to fit.
  [:&.leading {:flex-grow "11"}]
  [:&.trailing {:flex-grow "9"}])

;; With nothing of the drawer above it — the tray shut and no search open —
;; the strip is the top of the drawer, so its tabs take the tray's rounded
;; edge.
(o/defstyled tab-strip :div
  {:flex "none"
   :display "flex"}
  [:&:first-child
   [tab
    {:box-shadow "inset 0 1px 0 var(--property-3), inset 1px 0 0 var(--property-3), inset -1px 0 0 var(--property-3)"}
    [:&.leading {:border-top-left-radius "16px"}]
    [:&.trailing {:border-top-right-radius "16px"}]
    [:&.behind {:box-shadow "inset 0 0 0 1px var(--property-3)"}]]])

;; A glyph and what it adds in a word, the ＋ on its corner. Set back from the
;; tab's right edge and raised a touch off its middle; positioned, so it lies
;; over the tab's press.
(o/defstyled tab-add :button
  {:position "relative" :top "-1.5px"
   :flex "none" :align-self "center"
   :height "22px" :margin-right "14px" :padding "0 10px 0 5px"
   :display "inline-flex" :align-items "center" :gap "4px"
   :border "none" :border-radius "11px"
   :background "transparent"
   :box-shadow "inset 0 0 0 1px var(--property-3)"
   :font-family "inherit" :font-size "11px" :font-weight "700"
   :white-space "nowrap"
   :color t/--card-forest
   :cursor "pointer"}
  [:svg {:flex "none" :width "13px" :height "13px" :fill "currentColor"}]
  [:.tab-add-plus
   {:position "absolute" :right "-3px" :bottom "-3px"
    :width "11px" :height "11px"
    :display "flex" :align-items "center" :justify-content "center"
    :border-radius "50%"
    :background t/--card-surface
    :box-shadow "0 0 0 1px var(--card-sage-light)"}
   [:svg {:width "7px" :height "7px"}]]
  [:&:hover {:box-shadow "inset 0 0 0 1px var(--card-forest)"}]
  ["&[aria-expanded='true']"
   {:background t/--property-3
    :box-shadow "inset 0 0 0 1px var(--card-forest)"}])

;;
;; What the task relates to
;;

(def ^:private entity-uuid
  (some-fn :task/uuid :note/uuid :contact/uuid :group/uuid :space/uuid))

(defn- related-uuids
  [{:keys [tasks notes contacts tags spaces]} task-uuid]
  (cond-> (into #{} (keep entity-uuid) (concat tasks notes contacts tags spaces))
    task-uuid (conj task-uuid)))

(defn- add!
  [{:keys [draft change!]} kind entity]
  (let [field (case kind
                :task :tasks
                :note :notes
                :contact :contacts
                (:tag :create-tag) :tags
                :space :spaces)]
    (change! {field (conj (vec (get draft field)) entity)})))

(defn- add-link!
  [{:keys [draft change!]} link-url]
  (let [urls (:urls draft)]
    (when-not (some #{link-url} urls)
      (change! {:urls (conj (vec urls) link-url)}))))

(defn- tray-groups
  "The tray's groups, in order, each a heading over its chips; a kind the task
   has none of is left out, bar the links while the host `page` can be added."
  [{:keys [draft change! editable? spaces? on-open-entity] :as props} page]
  (let [{:keys [urls tasks notes contacts tags spaces]} draft
        page-url (:url page)
        match #(url-scope/link-match page %)
        remover (fn [field id-key]
                  (when editable?
                    (fn [item]
                      (change! {field (vec (remove #(= (id-key item) (id-key %))
                                                   (get draft field)))}))))
        offer-page? (and editable? page-url (not-any? #(= :page (match %)) urls))]
    (cond-> []
      (or (seq urls) offer-page?)
      (conj {:label "Links"
             :icon [icons/outlined-link]
             :chips (cond-> (vec (for [link-url (sort-by (comp related/match-rank match) urls)]
                                   ^{:key link-url}
                                   [sections/link-chip {:url link-url
                                                        :on-remove (remover :urls identity)}]))
                      offer-page?
                      (conj ^{:key "add-page"}
                       [add-page-button
                        {:type "button"
                         :title (str "Link " (url/url->display-label page-url) " to this task")
                         :on-click #(add-link! props page-url)}
                        [icons/outlined-add]
                        [:span "Add this page"]]))})

      (seq tasks)
      (conj {:label "Tasks"
             :icon [icons/humble-task]
             :chips (for [linked-task tasks]
                      ^{:key (:task/uuid linked-task)}
                      [sections/task-chip {:task linked-task
                                           :on-remove (remover :tasks :task/uuid)
                                           :on-open on-open-entity}])})

      (seq notes)
      (conj {:label "Notes"
             :icon [icons/humble-note]
             :chips (for [note notes]
                      ^{:key (:note/uuid note)}
                      [sections/note-chip {:note note
                                           :on-remove (remover :notes :note/uuid)
                                           :on-open on-open-entity}])})

      (seq contacts)
      (conj {:label "Contacts"
             :icon [contact-icon]
             :chips (for [contact contacts]
                      ^{:key (:contact/uuid contact)}
                      [sections/contact-chip {:contact contact
                                              :on-remove (remover :contacts :contact/uuid)
                                              :on-open on-open-entity}])})

      (seq tags)
      (conj {:label "Tags"
             :icon [icons/humble-tag]
             :chips (for [tag tags]
                      ^{:key (sections/tag-key tag)}
                      [sections/tag-chip {:tag tag
                                          :on-remove (remover :tags identity)}])})

      (seq spaces)
      (conj {:label "Spaces"
             :icon [space-icon]
             :chips (for [space spaces]
                      ^{:key (:space/uuid space)}
                      [sections/space-chip {:space space
                                            :on-remove (when spaces? (remover :spaces :space/uuid))
                                            :on-open on-open-entity}])}))))

;;
;; Searching for more
;;

(def ^:private hit-kinds [:task :note :contact :tag :space])

(defn- one-line [text]
  (-> (or text "") (str/replace #"\s+" " ") str/trim))

(defn- hit-row
  [kind entity]
  (let [full-name (not-empty (:contact/full-name entity))
        email (not-empty (first (:contact/email entity)))]
    (assoc (case kind
             :task {:label (or (not-empty (one-line (:task/title entity))) "Untitled task")}
             :note {:label (or (not-empty (one-line (:note/text entity))) "Untitled note")}
             :contact {:label (contacts/->label entity)
                       :sub (when full-name email)}
             :tag {:label (tag-ui/with-hash (:group/display-name entity))}
             :space {:label (or (not-empty (one-line (:space/title entity))) "Untitled space")})
           :kind kind
           :entity entity
           :key (entity-uuid entity))))

(defn- result-rows
  "The hits not already related, one of each kind in turn, so that no kind
   crowds out the rest."
  [hits related]
  (let [rows-by-kind (for [kind hit-kinds]
                       (into []
                             (comp (remove #(contains? related (entity-uuid %)))
                                   (map #(hit-row kind %)))
                             (get hits kind)))]
    (->> (range (apply max 0 (map count rows-by-kind)))
         (mapcat (fn [index] (keep #(get % index) rows-by-kind))))))

(defn- create-tag-row
  "The row creating a tag named `text`, unless a tag found or on the task is
   already called that."
  [text found-tags related-tags]
  (let [wanted (str/lower-case text)
        taken? (some #(= wanted (some-> (or (:group/display-name %) (:tag/name %))
                                        str/lower-case))
                     (concat found-tags related-tags))]
    (when-not taken?
      {:kind :create-tag
       :key "create-tag"
       :entity {:tag/name text}
       :label (str "Create " (tag-ui/with-hash text))})))

(defn- typed-link
  "The link `text` makes when it reads as an address — a host with a
   top-level domain — rather than a search."
  [text]
  (let [trimmed (str/trim text)]
    (when (re-find #"^(https?://)?[^\s@/]+\.[a-z]{2,}(/\S*)?$" (str/lower-case trimmed))
      (url/normalize-http-url trimmed))))

(defn- row-glyph [{:keys [kind entity]}]
  (case kind
    :task [icons/humble-task]
    :note [icons/humble-note]
    :contact [(contacts/type-icon entity)]
    (:tag :create-tag) [icons/humble-tag]
    :space [space-icon]))

(defn- row-tint [{:keys [kind]}]
  (if (= :create-tag kind) "tag" (name kind)))

(defn- results
  "The rows found for what is typed, best nearest the field; `index` is the
   live row, counted from there. Its ✕ closes the search."
  [{:keys [rows index on-pick on-hover on-close]}]
  [parts/results-band
   [:div.results-top
    [:div.results-head "Suggestions"]
    [:button.results-close
     {:type "button"
      :aria-label "Close the search"
      :title "Close the search"
      :on-mouse-down #(.preventDefault %)
      :on-click on-close}
     [icons/outlined-close {:size 13}]]]
   (if (seq rows)
     (into [:div.results-rows]
           (for [[row-index row] (reverse (map-indexed vector rows))]
             ^{:key (:key row)}
             [parts/result-row
              {:type "button"
               :class (when (= row-index index) "on")
               ;; Picked on mouse-down so the field keeps the caret.
               :on-mouse-down (fn [e]
                                (.preventDefault e)
                                (on-pick row))
               :on-mouse-enter #(on-hover row-index)}
              [related/tile {:class (row-tint row)} (row-glyph row)]
              [:span.result-text
               [:span.result-label (:label row)]
               (when-let [sub (:sub row)]
                 [:span.result-sub sub])]]))
     [:div.results-quiet "Nothing matches."])])

(defn- add-field
  "The field adding to what the task relates to: it searches tasks, notes,
   contacts, tags and spaces as you type, adds a pasted link, and creates a
   tag it can't find. `on-close`, while the field can be put away, does so.
   What is typed and found is kept under `drawer-key`
   ([[hai.tasks.drawer.data]]), and goes with the field."
  [{:keys [drawer-key draft task-uuid spaces? on-close] :as props}]
  (r/with-let [!input (atom nil)
               focus-input! #(some-> @!input .focus)]
    (let [{:keys [text found list-open?] :as search} @(<s [::data/search drawer-key :related-search])
          trimmed (str/trim text)
          {found-text :text hits :hits} found
          fresh? (= found-text trimmed)
          link-url (typed-link text)
          create-row (when (and fresh? (not link-url))
                       (create-tag-row trimmed (:tag hits) (:tags draft)))
          rows (cond-> (vec (take (cond-> result-limit create-row dec)
                                  (result-rows hits (related-uuids draft task-uuid))))
                 create-row (conj create-row))
          index (max 0 (min (:index search) (dec (count rows))))
          listing? (and list-open? (or (seq rows) (and fresh? (not link-url))))
          point! #(>e [::data/point drawer-key :related-search %])
          close-list! #(>e [::data/close-list drawer-key :related-search])
          clear! #(>e [::data/clear-text drawer-key :related-search])
          pick! (fn [{row-kind :kind entity :entity}]
                  (add! props row-kind entity)
                  (clear!)
                  (focus-input!))
          add-typed-link! (fn []
                            (add-link! props link-url)
                            (clear!)
                            (focus-input!))]
      ;; Keyed, so the field stays the same element — and keeps the caret — as
      ;; the list mounts above it and unmounts.
      [:<>
       (when listing?
         ^{:key "results"}
         [results {:rows rows
                   :index index
                   :on-pick pick!
                   :on-hover point!
                   :on-close (or on-close close-list!)}])
       ^{:key "field"}
       [parts/field-frame
        {:on-click (fn [e]
                     (when (= (.-target e) (.-currentTarget e))
                       (focus-input!)))}
        [:input.field-input.add-relationship
         {:ref #(reset! !input %)
          :value text
          :placeholder "Add a relationship"
          :aria-label "Add a relationship: search tasks, notes, contacts and tags, or paste a link"
          :auto-complete "off"
          :spell-check false
          ;; At once: a render ahead of a queued keystroke would put older text
          ;; back in the box.
          :on-change #(>e! [::data/type-text drawer-key :related-search (.. % -target -value) spaces?])
          ;; A press on the field shows or hides what was found for it.
          :on-click #(>e [::data/press-field drawer-key :related-search])
          :on-blur #(>e [::data/leave-field drawer-key :related-search])
          :on-key-down (fn [e]
                         (case (.-key e)
                           ;; The list grows away from the field, so ↑ walks
                           ;; away from it.
                           "ArrowUp" (when (and listing? (seq rows))
                                       (.preventDefault e)
                                       (point! (min (inc index) (dec (count rows)))))
                           "ArrowDown" (when (and listing? (seq rows))
                                         (.preventDefault e)
                                         (point! (max 0 (dec index))))
                           "Enter" (do (.preventDefault e)
                                       (cond
                                         (and listing? (seq rows)) (pick! (nth rows index))
                                         link-url (add-typed-link!)))
                           ;; The first closes the list. The next puts the
                           ;; field away, what is typed with it, or where it
                           ;; stays, clears it and leaves it.
                           "Escape" (do (.preventDefault e)
                                        (.stopPropagation e)
                                        (cond
                                          list-open? (close-list!)
                                          on-close (on-close)
                                          :else (do (clear!)
                                                    (.. e -target blur))))
                           nil))}]
        (when link-url
          [add-link-button
           {:type "button"
            :title "Add this link"
            :on-mouse-down #(.preventDefault %)
            :on-click add-typed-link!}
           [icons/outlined-link]
           [:span "Add"]])]])
    (finally
      (>e [::data/reset-search drawer-key :related-search]))))

;;
;; The drawer
;;

(defn- tab-button
  "One of the drawer's tabs, `content` on it. Pressed while `shown?`, it shuts
   or opens the tray; otherwise it turns the drawer to the tab, `noun` naming
   what that shows. `beside`, if given, stands after the content, pressed on
   its own. `add`, where the viewer may add on the tab, is its add button: the
   `glyph` and `label` on it, whether its search is open (`searching?`), what
   a press does in words (`hint`), `on-add` and the `ref` its element goes
   to."
  [{:keys [shown? open? position noun on-press beside add]} & content]
  [tab {:class (str/join " " (remove nil? [(name position)
                                           (when-not shown? "behind")
                                           (when add "with-add")]))}
   [:div.tab-face
    (into [:button.tab-press {:type "button"
                              :aria-expanded (and shown? open?)
                              :title (cond
                                       (not shown?) (str "Show " noun)
                                       open? "Collapse"
                                       :else "Expand")
                              :on-click on-press}]
          content)
    beside]
   (when-let [{:keys [glyph label searching? hint on-add ref]} add]
     [tab-add {:type "button"
               :ref ref
               :aria-expanded (boolean searching?)
               :aria-label hint
               :title hint
               :on-click on-add}
      glyph
      [:span label]
      [:span.tab-add-plus {:aria-hidden true} [icons/outlined-add]]])])

(defn- tabs
  "The tabs along the drawer's foot, Related and Share. `on-press` gets the
   tab pressed. `related-add`, where the viewer may add to what the task
   relates to, holds whether Related's search is out (`:searching?`), and its
   add button's `:on-add` and `:ref`; Share's search opens from the task's
   header. `on-open-entity` goes to the related pill."
  [{:keys [draft shared-count shown open? related-add on-press on-open-entity]}]
  (let [private? (zero? shared-count)
        {:keys [urls tasks notes contacts tags spaces]} draft
        tab-props (fn [tab-key position noun]
                    {:shown? (= tab-key shown)
                     :open? open?
                     :position position
                     :noun noun
                     :on-press #(on-press tab-key)})]
    [tab-strip
     [tab-button (assoc (tab-props :related :leading "what this relates to")
                        :beside [related/component
                                 {:related {:link-urls urls
                                            :tasks tasks
                                            :notes notes
                                            :contacts contacts
                                            :tags tags
                                            :spaces spaces}
                                  :on-open-entity on-open-entity}]
                        :add (when related-add
                               (assoc related-add
                                      :glyph [icons/outlined-link]
                                      :label "Add link"
                                      :hint (if (:searching? related-add)
                                              "Fold the search away"
                                              "Search or add"))))
      [:span.tab-label "Related"]]
     [tab-button (tab-props :share :trailing "who this is shared with")
      [:span.tab-glyph (if private? [icons/outlined-lock] [share-icon])]
      [:span.tab-label (if private? "Private" "Share")]
      (when-not private?
        [:span.tab-count shared-count])]]))

(defn- reveal-share!
  "Scrolls `tray` until the row of the share under `share-key` sits at
   mid-height, and flashes the row."
  [^js tray share-key]
  (when-let [^js row (some-> tray (.querySelector (str "[data-share-key=\"" (js/CSS.escape share-key) "\"]")))]
    (let [row-box (.getBoundingClientRect row)
          tray-box (.getBoundingClientRect tray)
          row-middle (+ (.-top row-box) (/ (.-height row-box) 2))
          tray-middle (+ (.-top tray-box) (/ (.-height tray-box) 2))]
      (set! (.-scrollTop tray) (+ (.-scrollTop tray) (- row-middle tray-middle)))))
  (>e [::share/flash share-key]))

(defn- first-row-height
  "The floor of a drag on `tray`: the height that shows the first row of its
   list whole. It ends where the second row begins, or at the first row's foot
   and the tray's padding under it, whichever comes first."
  [^js tray]
  (let [groups (array-seq (.querySelectorAll tray ".tray-group"))
        ;; A group is its heading over its rows.
        rows (mapcat (fn [^js group]
                       (cons (.-firstElementChild group)
                             (array-seq (.. group -lastElementChild -children))))
                     groups)
        ^js first-row (or (some-> ^js (first groups) .-lastElementChild .-firstElementChild)
                          (.querySelector tray ".tray-empty"))]
    (if-not first-row
      tray-strip-height
      (let [first-foot (.. first-row getBoundingClientRect -bottom)
            padding (js/parseFloat (.-paddingBottom (js/getComputedStyle tray)))
            next-tops (->> rows
                           (map (fn [^js row] (.. row getBoundingClientRect -top)))
                           (filter #(>= % (- first-foot 0.5))))
            end (apply min (+ first-foot padding) next-tops)]
        (js/Math.ceil (+ (- end (.. tray getBoundingClientRect -top))
                         (.-scrollTop tray)))))))

(defn- dragged-height
  "The height a drag to `wanted` leaves the tray at: as dragged, down to its
   `floor`; below that the floor or the strip, whichever is nearer."
  [wanted floor]
  (cond
    (>= wanted floor) wanted
    (< wanted (/ (+ floor tray-strip-height) 2)) tray-strip-height
    :else floor))

(defn- fitted-height
  "The height a `tray` nobody has dragged takes: enough for the whole of its
   list, no less than its first row and no more than half the card it stands
   at the foot of ([[hai.tasks.form]]). The whole is read with the tray shut
   to nothing, since a tray taller than its list answers with its own height."
  [^js tray]
  (let [^js card (.closest tray ".task-card-layout")
        style (.-style tray)
        height-as-set (.-height style)
        _ (set! (.-height style) "0px")
        whole (.-scrollHeight tray)
        _ (set! (.-height style) height-as-set)]
    (max (first-row-height tray)
         (cond-> whole
           card (min (js/Math.floor (/ (.-clientHeight card) 2)))))))

(defn- description-above
  "The description `tray` shares its card with, in [[hai.tasks.form]]'s card
   layout."
  [^js tray]
  (some-> (.closest tray ".task-card-layout")
          (.querySelector ".task-body")))

(defn- covered?
  "Whether the tray has left `description` nothing but its strip."
  [^js description]
  (<= (.-clientHeight description) (inc description-strip-height)))

(defn- uncovering-height
  "The height that takes `tray` down far enough to show `description` whole,
   though no further than brings the tray's top `tray-rest-mark` of the way
   down the card, and never under its floor."
  [^js tray ^js description]
  (let [card-box (.getBoundingClientRect (.closest tray ".task-card-layout"))
        mark (+ (.-top card-box) (* tray-rest-mark (.-height card-box)))
        tray-top (.. tray -previousElementSibling getBoundingClientRect -top)
        hidden (- (.-scrollHeight description) (.-clientHeight description))]
    (max (first-row-height tray)
         (- (.. tray getBoundingClientRect -height)
            (min hidden (max 0 (- mark tray-top)))))))

(defn- more-below?
  "Whether more of `tray`'s list lies below what it shows."
  [^js tray]
  (< (+ (.-scrollTop tray) (.-clientHeight tray))
     (dec (.-scrollHeight tray))))

(defn- related-groups
  [props page]
  (for [{:keys [chips] :as group} (tray-groups props page)]
    (assoc group :body (into [sections/chip-row {:class "centered"}] chips))))

(defn component
  "The drawer on the task in `draft`, as the task form stages it. Its Related
   tab is what the task relates to, with changes through `change!` while
   `editable?`, its links placed against the host page, which it offers to
   link; `on-open-entity` opens a linked task, note, contact or space, and
   `spaces?` turns on adding and removing spaces. Its Share tab is who the
   task is shared with: a saved `task`'s shares, or else those the draft
   stages through `change!`. While `editable?`, the open Related drawer ends
   in the field that adds to it, and its tab's add button puts the caret
   there; with the drawer shut, that button brings the field out on its own
   above the tabs and puts it away. The open Share drawer ends in its search
   for anyone who may share, and the Share button in the task's header
   ([[hai.tasks.mini-app]]) works on it the same way. A new task's drawer
   starts open, and so does a saved task's but in the web-app's mobile layout
   ([[hai.ui.viewport]]), where it starts shut, its tabs alone under the
   description.

   What it is left showing is kept in app-db under `drawer-key`
   ([[hai.tasks.drawer.data]]), and forgotten when it goes; a host keys it by
   `drawer-key` too, so another task's is a drawer of its own."
  [{:keys [drawer-key task draft change! editable? on-open-entity] :as props}]
  (r/with-let [!related-add (atom nil)
               related-add-ref #(reset! !related-add %)
               close-search! #(>e [::data/close-search drawer-key])
               !frame (atom nil)
               attach-frame! #(reset! !frame %)
               ;; The header's Share, which opens Share's search
               ;; ([[hai.tasks.mini-app]]), by the key it carries: in the
               ;; drawer's own document, or the shadow root it is in.
               share-button #(some-> ^js @!frame
                                     .getRootNode
                                     (.querySelector (str "[data-drawer-share=\""
                                                          (js/CSS.escape (pr-str drawer-key))
                                                          "\"]")))
               related-field #(some-> ^js @!frame (.querySelector ".add-relationship"))
               ;; Related's field takes the caret: at once where it is there,
               ;; since a touch keyboard rises only for the press itself, else
               ;; once the press has brought it.
               focus-related-field! (fn []
                                      (if-let [^js field (related-field)]
                                        (.focus field)
                                        (r/after-render #(some-> ^js (related-field) .focus))))
               fold-related! #(>e [::data/fold-related drawer-key])
               toggle! #(>e [::data/toggle drawer-key])
               press-tab! #(>e [::data/press-tab drawer-key %])
               !tray (atom nil)
               !tray-observer (atom nil)
               !drag (r/atom nil)
               ;; Where the tray was dragged to, for as long as this task is
               ;; open. Until it is dragged the tray fits its list.
               !dragged-height (r/atom nil)
               !fitted-height (r/atom nil)
               !more-below? (r/atom false)
               !covering? (r/atom false)
               note-more-below! (fn [& _]
                                  (when-let [tray @!tray]
                                    (let [more? (more-below? tray)]
                                      (when (not= more? @!more-below?)
                                        (reset! !more-below? more?)))))
               ;; After anything that can change the tray's list: a render, a
               ;; change of its size.
               measure-tray! (fn [& _]
                               (when-let [tray @!tray]
                                 (when (nil? @!dragged-height)
                                   (let [fitted (fitted-height tray)]
                                     (when (not= fitted @!fitted-height)
                                       (reset! !fitted-height fitted))))
                                 (let [covering? (boolean (some-> (description-above tray) covered?))]
                                   (when (not= covering? @!covering?)
                                     (reset! !covering? covering?))))
                               (note-more-below!))
               attach-tray! (fn [^js tray]
                              (some-> ^js @!tray-observer .disconnect)
                              (reset! !tray tray)
                              (when tray
                                ;; Fitted on the element itself as it mounts,
                                ;; so it is never painted at another height.
                                (when (nil? @!dragged-height)
                                  (let [fitted (fitted-height tray)]
                                    (set! (.. tray -style -height) (str fitted "px"))
                                    (reset! !fitted-height fitted)))
                                ;; The description is watched too: a card that
                                ;; grows gives it room without the tray moving.
                                (let [observer (js/ResizeObserver. measure-tray!)]
                                  (.observe observer tray)
                                  (some->> (description-above tray) (.observe observer))
                                  (reset! !tray-observer observer))))
               ;; On by a view, less the strip at the foot and a line to keep
               ;; the place by.
               page-tray! (fn []
                            (when-let [^js tray @!tray]
                              (.scrollTo tray #js {:top (+ (.-scrollTop tray)
                                                           (max 8 (- (.-clientHeight tray) 40)))
                                                   :behavior "smooth"})))
               shown-height #(some-> ^js @!tray .getBoundingClientRect .-height)
               start-drag! (fn [^js e]
                             ;; A press on the row's ✕ is the ✕'s own.
                             (when-not (.closest (.-target e) "button")
                               (when-let [height (shown-height)]
                                 (.preventDefault e)
                                 (.setPointerCapture (.-currentTarget e) (.-pointerId e))
                                 ;; The floor is measured at the press: a row is a
                                 ;; line of chips on one tab and a person on the
                                 ;; other.
                                 (reset! !drag {:pointer-y (.-clientY e)
                                                :height height
                                                :floor (first-row-height @!tray)})
                                 (reset! !dragged-height height))))
               end-drag! (fn [_]
                           (when-let [{:keys [floor]} @!drag]
                             (let [shown (shown-height)
                                   dragged @!dragged-height]
                               (reset! !drag nil)
                               (reset! !dragged-height
                                       (if (= tray-strip-height dragged)
                                         dragged
                                         (max floor
                                              (cond-> dragged shown (min shown))))))))
               ;; A minimised tray's list comes back as it would have opened.
               restore-tray! #(reset! !dragged-height nil)
               uncover! (fn []
                          (when-let [tray @!tray]
                            (when-let [description (description-above tray)]
                              (reset! !dragged-height
                                      (uncovering-height tray description)))))
               drag! (fn [^js e]
                       (when-let [{:keys [pointer-y height floor]} @!drag]
                         ;; A release the sidebar never heard — let go over the
                         ;; host page — leaves nothing pressed: that ends the
                         ;; drag rather than a hover resizing the tray.
                         (if (zero? (.-buttons e))
                           (end-drag! e)
                           (let [pointer (.-clientY e)
                                 shown (shown-height)
                                 wanted (+ height (- pointer-y pointer))]
                             ;; Past the room the card leaves it the tray stops
                             ;; growing; the drag restarts from there, so moving
                             ;; back shrinks it at once.
                             (if (and shown
                                      (< shown (- @!dragged-height 0.5))
                                      (> wanted shown))
                               (do (swap! !drag assoc :pointer-y pointer :height shown)
                                   (reset! !dragged-height (max floor shown)))
                               (reset! !dragged-height (dragged-height wanted floor)))))))]
    (let [{open? :open? shown-tab :tab searched :search :as drawer} @(<s [::data/drawer drawer-key])
          ;; Closed from inside the search, the focus goes back to the button
          ;; that opened it.
          leave-search! (fn []
                          (when searched
                            (close-search!)
                            (some-> ^js (case searched
                                          :related @!related-add
                                          :share (share-button))
                                    .focus)))
          ;; At once, so Related's field is there for the caret on the next
          ;; render.
          press-add! (fn [tab]
                       (let [focus? (core/focuses-field? drawer tab)]
                         (>e! [::data/press-add drawer-key tab])
                         (when focus?
                           (focus-related-field!))))
          related-open? (and open? (= :related shown-tab))
          share-open? (and open? (= :share shown-tab))
          page @(<s [::host-page/current])
          current-user @(<s [:auth/identity])
          share-tab (if task
                      (share/task-sharing task current-user)
                      (share/draft-sharing draft change! current-user))
          can-share? (:can-share? share-tab)
          dragged-to @!dragged-height
          ;; None yet on the tray's first render: it is fitted as it mounts.
          height (or dragged-to @!fitted-height)
          minimised? (= tray-strip-height dragged-to)
          groups (when open?
                   (case shown-tab
                     :related (related-groups props page)
                     :share (share/groups share-tab)))]
      (r/after-render measure-tray!)
      [drawer-frame {:ref attach-frame!}
       (when (and open? @!covering?)
         [:button.description-more {:type "button"
                                    :aria-label "Show the description"
                                    :title "Show the description"
                                    :on-click uncover!}
          [more-icon]])
       (when open?
         [tray-head {:class (when @!drag "dragging")
                     :title "Drag to resize"
                     :on-pointer-down start-drag!
                     :on-pointer-move drag!
                     :on-pointer-up end-drag!
                     :on-pointer-cancel end-drag!
                     :on-lost-pointer-capture end-drag!}
          (case shown-tab
            :related [:span.tray-name
                      [:span "Related items"]]
            :share [:span.tray-name
                    (if (zero? (:shared-count share-tab)) [icons/outlined-lock] [share-icon])
                    [:span "Sharing"]])
          [:span.tray-handle]
          [:button.tray-close {:type "button"
                               :aria-label "Collapse"
                               :title "Collapse"
                               :on-click toggle!}
           [icons/outlined-close]]])
       (when open?
         [tray {:ref attach-tray!
                :class (when minimised? "minimised")
                :style (when height
                         {:height (str height "px")})
                :on-scroll note-more-below!}
          (if (seq groups)
            (into [:div.tray-groups]
                  (for [{:keys [label icon body]} groups]
                    ^{:key label}
                    [:div.tray-group
                     [group-heading
                      [:span.group-heading-icon icon]
                      label]
                     body]))
            [:div.tray-empty "Nothing here yet."])
          (cond
            minimised?
            [:button.tray-more {:type "button"
                                :aria-label "Show more"
                                :title "Show more"
                                :on-click restore-tray!}
             [more-icon]]

            @!more-below?
            [:div.tray-below
             [:button.tray-page {:type "button"
                                 :aria-label "Show more"
                                 :title "Show more"
                                 ;; Takes the press and not the caret: an open
                                 ;; search's field keeps it.
                                 :on-mouse-down #(.preventDefault %)
                                 :on-click page-tray!}
              [more-icon]]])])
       (cond
         ;; Related's field is always at the foot of its open drawer. With the
         ;; drawer shut it is there while its tab's add button has it out,
         ;; and that is when it can be put away.
         (and editable? (or related-open? (= :related searched)))
         ^{:key "related"}
         [search-row/component {:on-press-elsewhere (when-not related-open? fold-related!)}
          [add-field (assoc props
                            :task-uuid (:task/uuid task)
                            :on-close (when-not related-open? leave-search!))]]

         ;; Share's search likewise, for anyone who may share.
         (and can-share? (or share-open? (= :share searched)))
         ^{:key "share"}
         [search-row/component {}
          [share-bar/component {:drawer-key drawer-key
                                :listed (:rows share-tab)
                                :on-add (:on-add share-tab)
                                :on-reveal #(reveal-share! @!tray %)
                                :on-close (when-not share-open? leave-search!)}]])
       [tabs {:draft draft
              :on-open-entity on-open-entity
              :shared-count (:shared-count share-tab)
              :shown shown-tab
              :open? open?
              :related-add (when editable?
                             {:searching? (= :related searched)
                              :on-add #(press-add! :related)
                              :ref related-add-ref})
              :on-press press-tab!}]])
    (finally
      (some-> ^js @!tray-observer .disconnect)
      (>e [::data/forget drawer-key]))))
